import React, { useMemo, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { FiPhone } from 'react-icons/fi';

import { Button, Modal, PrimaryButton } from '..';
import PhoneVerification from '../PhoneVerification';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { FormattedMessage } from '../../util/reactIntl';
import {
  getUserPhoneData,
  hasSmsConsentForPhone,
  isPhoneVerifiedForUser,
  maskPhoneNumber,
  normalizePhoneNumber,
  normalizePhoneNumberToE164,
} from '../../util/phoneNumber';
import { getShippingDetails } from '../../util/userDataExtractor';

import css from './SmsVerificationPrompt.module.css';

const COMPONENT_ID = 'SmsVerificationPrompt';
const CHECKOUT_PATH_SEGMENT = '/checkout';

const noop = () => null;

const normalizePromptPhoneNumber = phoneNumber => {
  if (typeof phoneNumber !== 'string') {
    return '';
  }

  const e164PhoneNumber = normalizePhoneNumberToE164(phoneNumber);
  return normalizePhoneNumber(phoneNumber) || e164PhoneNumber.trim() || '';
};

const getProfilePrivateData = currentUser => currentUser?.attributes?.profile?.privateData || {};

export const getSmsVerificationPromptPrivateData = currentUser => {
  const privateData = getProfilePrivateData(currentUser);
  return privateData.smsVerificationPrompt || {};
};

export const getSavedSmsPhoneNumber = currentUser => {
  const shippingPhoneNumber = getShippingDetails(currentUser)?.phoneNumber;
  const profilePhoneNumber = getUserPhoneData(currentUser).phoneNumber;

  return normalizePromptPhoneNumber(shippingPhoneNumber || profilePhoneNumber || '');
};

export const isCheckoutPath = pathname =>
  typeof pathname === 'string' && pathname.includes(CHECKOUT_PATH_SEGMENT);

export const shouldShowSmsVerificationPrompt = params => {
  const { isAuthenticated, currentUser, pathname, sessionDismissed = false } = params || {};
  const savedPhoneNumber = getSavedSmsPhoneNumber(currentUser);
  const promptPrivateData = getSmsVerificationPromptPrivateData(currentUser);

  return Boolean(
    isAuthenticated &&
      currentUser?.id &&
      savedPhoneNumber &&
      !sessionDismissed &&
      !promptPrivateData.dismissed &&
      !isPhoneVerifiedForUser(currentUser, savedPhoneNumber) &&
      // Already opted in to texts for this number at checkout
      !hasSmsConsentForPhone(currentUser, savedPhoneNumber) &&
      !isCheckoutPath(pathname)
  );
};

export const buildSmsVerificationDismissalPayload = (currentUser, dismissedAt) => {
  const existingPrivateData = getProfilePrivateData(currentUser);
  const existingPromptPrivateData = existingPrivateData.smsVerificationPrompt || {};

  return {
    privateData: {
      ...existingPrivateData,
      smsVerificationPrompt: {
        ...existingPromptPrivateData,
        dismissed: true,
        dismissedAt,
      },
    },
  };
};

export const buildSmsVerificationPreferencesPayload = currentUser => {
  const existingPrivateData = getProfilePrivateData(currentUser);
  const existingNotificationPreferences = existingPrivateData.notificationPreferences || {};

  return {
    privateData: {
      ...existingPrivateData,
      notificationPreferences: {
        ...existingNotificationPreferences,
        ordersSms: true,
        offersSms: true,
      },
    },
  };
};

export const didProfileUpdateFail = result => result?.type?.endsWith('/rejected');

const SmsVerificationPromptComponent = props => {
  const {
    className = null,
    currentUser = null,
    isAuthenticated,
    pathname = null,
    onManageDisableScrolling,
    onRefreshCurrentUser,
    onUpdateProfile,
    usePortal = true,
  } = props;

  const savedPhoneNumber = useMemo(() => getSavedSmsPhoneNumber(currentUser), [currentUser]);
  const maskedPhoneNumber = maskPhoneNumber(savedPhoneNumber) || savedPhoneNumber;
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [showVerificationFlow, setShowVerificationFlow] = useState(false);
  const [savingDismissal, setSavingDismissal] = useState(false);
  const [dismissalError, setDismissalError] = useState(null);
  const [verifiedInSession, setVerifiedInSession] = useState(false);

  const isEligible = shouldShowSmsVerificationPrompt({
    isAuthenticated,
    currentUser,
    pathname,
    sessionDismissed,
  });
  const isOpen = isEligible && !verifiedInSession;

  const refreshCurrentUser = () => {
    if (onRefreshCurrentUser) {
      return onRefreshCurrentUser({ enforce: true, updateNotifications: false });
    }
    return Promise.resolve();
  };

  const handleDismiss = async () => {
    if (savingDismissal) {
      return;
    }

    setSavingDismissal(true);
    setDismissalError(null);

    try {
      const dismissedAt = new Date().toISOString();
      const updateResult = await onUpdateProfile(
        buildSmsVerificationDismissalPayload(currentUser, dismissedAt)
      );

      if (didProfileUpdateFail(updateResult)) {
        throw updateResult?.payload || updateResult?.error || new Error('Profile update failed.');
      }

      setSessionDismissed(true);
      setShowVerificationFlow(false);
    } catch (e) {
      setDismissalError(e);
    } finally {
      setSavingDismissal(false);
    }
  };

  const handleVerified = async () => {
    setVerifiedInSession(true);
    setShowVerificationFlow(false);
    setDismissalError(null);

    try {
      const updateResult = await onUpdateProfile(
        buildSmsVerificationPreferencesPayload(currentUser)
      );

      if (didProfileUpdateFail(updateResult)) {
        throw updateResult?.payload || updateResult?.error || new Error('Profile update failed.');
      }
    } catch (e) {
      // Phone verification has already succeeded. Keep the prompt closed, then
      // refresh so protectedData.phoneNumberVerified wins future eligibility.
      setDismissalError(e);
    }

    try {
      await refreshCurrentUser();
    } catch (_e) {
      // Verification has already succeeded. The session flag prevents the prompt
      // from reopening if refreshing currentUser fails temporarily.
    }
  };

  const showDismissalError = Boolean(dismissalError);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      id={COMPONENT_ID}
      className={classNames(css.root, className)}
      scrollLayerClassName={css.scrollLayer}
      containerClassName={css.container}
      contentClassName={css.content}
      isOpen={isOpen}
      onClose={noop}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal={usePortal}
      hideCloseButton
    >
      <section
        className={css.prompt}
        aria-labelledby={`${COMPONENT_ID}-title`}
        aria-describedby={`${COMPONENT_ID}-description`}
      >
        <div className={css.iconBadge} aria-hidden="true">
          <FiPhone />
        </div>

        <h2 id={`${COMPONENT_ID}-title`} className={css.title}>
          <FormattedMessage
            id="SmsVerificationPrompt.title"
            defaultMessage="Verify your phone number"
          />
        </h2>

        <p id={`${COMPONENT_ID}-description`} className={css.description}>
          <FormattedMessage
            id="SmsVerificationPrompt.description"
            defaultMessage={
              'Verify your phone number to receive important SMS notifications about orders, ' +
              'offers, and account updates.'
            }
          />
        </p>

        {showVerificationFlow ? (
          <div className={css.verificationFlow}>
            <PhoneVerification
              className={css.phoneVerification}
              currentUser={currentUser}
              initialPhoneNumber={savedPhoneNumber}
              onVerified={handleVerified}
              compact
              showFrame={false}
              showTitle={false}
              showVerifiedSummary={false}
            />

            {showDismissalError ? (
              <p className={css.error} role="alert">
                <FormattedMessage
                  id="SmsVerificationPrompt.dismissError"
                  defaultMessage="We could not save that preference. Please try again."
                />
              </p>
            ) : null}

            <Button
              type="button"
              rootClassName={css.secondaryButton}
              inProgress={savingDismissal}
              disabled={savingDismissal}
              onClick={handleDismiss}
            >
              <FormattedMessage
                id="SmsVerificationPrompt.secondaryButton"
                defaultMessage="Maybe later"
              />
            </Button>
          </div>
        ) : (
          <>
            <div className={css.phoneGroup}>
              <span className={css.phoneLabel}>
                <FormattedMessage
                  id="SmsVerificationPrompt.phoneNumberLabel"
                  defaultMessage="Phone number"
                />
              </span>
              <div className={css.phoneValue}>
                <FiPhone aria-hidden="true" />
                <span>{maskedPhoneNumber}</span>
              </div>
            </div>

            {showDismissalError ? (
              <p className={css.error} role="alert">
                <FormattedMessage
                  id="SmsVerificationPrompt.dismissError"
                  defaultMessage="We could not save that preference. Please try again."
                />
              </p>
            ) : null}

            <div className={css.actions}>
              <PrimaryButton
                type="button"
                rootClassName={css.primaryButton}
                disabled={savingDismissal}
                onClick={() => {
                  setDismissalError(null);
                  setShowVerificationFlow(true);
                }}
              >
                <FormattedMessage
                  id="SmsVerificationPrompt.primaryButton"
                  defaultMessage="Verify phone number"
                />
              </PrimaryButton>

              <Button
                type="button"
                rootClassName={css.secondaryButton}
                inProgress={savingDismissal}
                disabled={savingDismissal}
                onClick={handleDismiss}
              >
                <FormattedMessage
                  id="SmsVerificationPrompt.secondaryButton"
                  defaultMessage="Maybe later"
                />
              </Button>
            </div>
          </>
        )}

        <p className={css.helperText}>
          <FormattedMessage
            id="SmsVerificationPrompt.helperText"
            defaultMessage={
              'You can manage your SMS notification preferences anytime from Account Settings.'
            }
          />
        </p>
      </section>
    </Modal>
  );
};

SmsVerificationPromptComponent.propTypes = {
  className: string,
  currentUser: object,
  isAuthenticated: bool.isRequired,
  pathname: string,
  onManageDisableScrolling: func.isRequired,
  onRefreshCurrentUser: func.isRequired,
  onUpdateProfile: func.isRequired,
  usePortal: bool,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const { currentUser } = state.user || {};

  return {
    currentUser,
    isAuthenticated,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onRefreshCurrentUser: options => dispatch(fetchCurrentUser(options)),
  onUpdateProfile: params => dispatch(updateProfile(params)),
});

export { SmsVerificationPromptComponent };

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SmsVerificationPromptComponent);
